import React, { useState } from "react";
import mixpanel from "mixpanel-browser";
import Amount from "../../../ui-components/molecules/amount/amount";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import usaStates from "../../../app/data/common/usaStates";
import PaymentService from "../../../app/data/payment/paymentService";
import { TransactionFeeQuote } from "../../../app/data/payment/PaymentSession";
import ProbillChipInput from "../../shipments/labels-receipts/probillChipInput";
import { ProbillEntry } from "../../shipments/labels-receipts/types";
import { PUBLIC_PAY_MAX_INVOICES, usePayableProbills } from "../../public-payment/usePayableProbills";
import "../../shipments/labels-receipts/labelsReceipts.scss";

const paymentService = PaymentService.getInstance();
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

const errorText = (fallback: string, getError?: () => string | object | null) => {
  const value = getError?.();
  return typeof value === "string" && value ? value : fallback;
};

const TransactionFeeCalculator: React.FC = () => {
  const { entries, setEntries, inputValue, setInputValue, groups, lookupError, validating } =
    usePayableProbills();
  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [cardBin, setCardBin] = useState("");
  const [quoting, setQuoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<TransactionFeeQuote | null>(null);

  const invoiceNumbers = entries
    .filter((entry) => entry.status === "valid" && entry.value != null)
    .map((entry) => entry.value as number);
  const subtotalCents = groups.reduce((sum, group) => sum + (group.openAmountCents || 0), 0);

  const onEntriesChange = (next: ProbillEntry[]) => {
    setEntries(next);
    setQuote(null);
  };

  const calculate = async () => {
    if (!invoiceNumbers.length) {
      setError("Add at least one probill with an open balance");
      return;
    }
    if (validating) {
      setError("Wait until the probill numbers finish checking");
      return;
    }
    if (!state) {
      setError("Billing state is required");
      return;
    }
    if (!ZIP_PATTERN.test(postalCode.trim())) {
      setError("Billing ZIP is required");
      return;
    }
    if (!/^\d{6,8}$/.test(cardBin)) {
      setError("Card BIN must be the first 6 or 8 digits of the card");
      return;
    }
    setQuoting(true);
    setError(null);
    const res = await paymentService.quoteTransactionFee({
      invoiceNumbers,
      billingAddress: {
        line1: line1.trim() || undefined,
        city: city.trim() || undefined,
        state,
        postalCode: postalCode.trim(),
      },
      cardBin,
    });
    setQuoting(false);
    if (!res.ok() || !res.data) {
      setQuote(null);
      setError(errorText("Failed to calculate the transaction fee", res.getError));
      return;
    }
    setQuote(res.data);
    mixpanel.track("payments:transaction_fee_calculated", {
      probill_count: invoiceNumbers.length,
      region: res.data.region,
    });
  };

  const stateOption = usaStates.find((option) => option.value === state) || null;

  return (
    <div className="xgs-payment-admin__fee">
      <div className="xgs-payment-admin__card">
        <h3 className="xgs-payment-admin__card__title">Probills</h3>
        <p className="xgs-payment-admin__fee-help">
          Type or paste XGS probill numbers. Each is checked automatically. The fee is calculated on the open balance.
          Up to {PUBLIC_PAY_MAX_INVOICES} probills.
        </p>
        <ProbillChipInput
          entries={entries}
          onEntriesChange={onEntriesChange}
          inputValue={inputValue}
          onInputValueChange={setInputValue}
          maxValid={PUBLIC_PAY_MAX_INVOICES}
          emptyPlaceholder="Type or paste XGS probill numbers…"
          addPlaceholder="Add another XGS probill number…"
          invalidFormatMessage="Enter an XGS probill number"
          capNoun="XGS probill numbers"
        />
        {lookupError && <XGSErrorMessage>{lookupError}</XGSErrorMessage>}
        {invoiceNumbers.length > 0 && (
          <p className="xgs-payment-admin__fee-subtotal">
            {invoiceNumbers.length} {invoiceNumbers.length === 1 ? "probill" : "probills"} · Open balance{" "}
            <Amount amount={subtotalCents / 100} />
          </p>
        )}
      </div>

      <div className="xgs-payment-admin__card">
        <h3 className="xgs-payment-admin__card__title">Billing address</h3>
        <p className="xgs-payment-admin__fee-help">
          State and ZIP are required. InterPayments uses both to decide the surcharge.
        </p>
        <div className="xgs-payment-admin__fee-address">
          <LabeledTextInput
            label="Address"
            labelMode={LabelModes.column}
            value={line1}
            onChange={(event) => setLine1(event.target.value)}
            placeholder="Street address"
          />
          <LabeledTextInput
            label="City"
            labelMode={LabelModes.column}
            value={city}
            onChange={(event) => setCity(event.target.value)}
            placeholder="City"
          />
          <LabeledSelectInput
            label="State"
            labelMode={LabelModes.column}
            required
            options={usaStates}
            value={stateOption}
            placeholder="Select state"
            onValueChange={(option) => setState(option && typeof option.value === "string" ? option.value : "")}
          />
          <LabeledTextInput
            label="ZIP"
            labelMode={LabelModes.column}
            required
            value={postalCode}
            onChange={(event) => setPostalCode(event.target.value)}
            placeholder="ZIP code"
          />
        </div>
        <LabeledTextInput
          label="Card BIN"
          labelMode={LabelModes.column}
          required
          className="xgs-payment-admin__fee-bin"
          value={cardBin}
          onChange={(event) => setCardBin(event.target.value.replace(/\D/g, "").slice(0, 8))}
          placeholder="First 6 or 8 digits"
        />
        <p className="xgs-payment-admin__fee-help">
          InterPayments uses the card BIN to decide whether a surcharge applies. Enter only the first 6 or 8 digits, not the full card number.
        </p>
        <Button
          theme={ButtonThemes.blue}
          type="button"
          spinner={quoting}
          disabled={quoting || validating}
          onClick={calculate}
        >
          Calculate fee
        </Button>
      </div>

      {error && <XGSErrorMessage>{error}</XGSErrorMessage>}

      {quote && (
        <div className="xgs-payment-admin__card">
          <h3 className="xgs-payment-admin__card__title">Transaction fee</h3>
          <dl className="xgs-payment-admin__fee-result">
            <div>
              <dt>Open balance</dt>
              <dd><Amount amount={quote.amountCents / 100} /></dd>
            </div>
            <div>
              <dt>Transaction fee</dt>
              <dd><Amount amount={quote.transactionFeeCents / 100} /></dd>
            </div>
            {quote.transactionFeePercent != null && (
              <div>
                <dt>Fee percent</dt>
                <dd>{Number(quote.transactionFeePercent).toFixed(2)}%</dd>
              </div>
            )}
            <div>
              <dt>Total</dt>
              <dd><Amount amount={quote.totalCents / 100} /></dd>
            </div>
            <div>
              <dt>Region</dt>
              <dd>{quote.region}</dd>
            </div>
          </dl>
          {quote.message && <p className="xgs-payment-admin__fee-help">{quote.message}</p>}
          <p className="xgs-payment-admin__fee-help">
            This is a quote from InterPayments. It does not charge a card.
          </p>
        </div>
      )}
    </div>
  );
};

export default TransactionFeeCalculator;
